/**
 * Reading order, found in the pixels — the lab's `findParts`/`paintIndex`, re-derived onto a fixed
 * coarse grid so the result is a bare byte array the shader can sample, not a canvas.
 *
 * Deck streams an artifact a piece at a time, so the resolve should arrive a piece at a time too.
 * Rather than any renderer hand-declaring its parts, the order is read off the rendered content: rows
 * of ink split the card into bands, gaps inside a band split it into columns, and the result is
 * numbered the way you would read it. That means it also works on a real artifact nobody wrote a
 * manifest for.
 *
 * Two adaptations from the lab, both forced by the input this runs on:
 *
 * 1. **"Ink" is divergence from the ground, not just alpha.** The lab's scenes were drawn onto a
 *    transparent canvas, so `alpha > 24` was the whole test. A snapshot of a real artifact is opaque
 *    to the last pixel — its ground is white, not absent — so on an opaque ground a pixel counts as
 *    ink when its colour diverges from the dominant corner colour. When the corners ARE transparent
 *    the lab's exact alpha rule applies unchanged.
 * 2. **Empty cells inherit the nearest ranked neighbour.** The lab left the background at 0, which
 *    made blank regions resolve FIRST (delay 0 leads every part). Here every cell ends up on some
 *    part's clock — a flood fill from all ranked cells — so nothing resolves early or last merely for
 *    being blank, and the delay field has no cliffs for the shader's linear filtering to ring on.
 *
 * Pure by construction: the input is typed structurally (`{ data, width, height }`) so the vitest node
 * environment can feed it plain objects — no DOM `ImageData` constructor anywhere near this file.
 */

export interface PartsGrid {
  readonly w: number;
  readonly h: number;
}

/**
 * 64x36 over the 1024x576 backing store = square 16px cells. Proportionally this is the lab's own
 * resolution: its 8px sampling step on a 1520x856 card put element bounds, not letterforms, on the
 * grid, and 16px of 1024 is the same altitude.
 */
export const DEFAULT_PARTS_GRID: PartsGrid = { w: 64, h: 36 };

export interface PartsMap {
  /** One byte per grid cell, row-major from the top-left: `round(255 * rank / count)`. */
  delays: Uint8Array;
  /** How many parts were found. 0 means an empty card — `delays` is all zeros and means nothing. */
  count: number;
}

/** Structural `ImageData` — the DOM one satisfies it, and so does a plain object in a node test. */
export interface ImageDataLike {
  readonly data: Uint8ClampedArray | Uint8Array;
  readonly width: number;
  readonly height: number;
}

/** The lab's exact alpha cutoff: below this a pixel is the transparent ground. */
const ALPHA_MIN = 24;
/**
 * Summed |Δr|+|Δg|+|Δb| against the ground colour that counts as ink on an opaque card. High enough
 * to swallow JPEG ripple on a flat ground (single-digit per channel), low enough that anti-aliased
 * type — whose core pixels sit near-black on white — clears it by an order of magnitude.
 */
const COLOR_MIN = 48;
/**
 * Grid cells of nothing that count as a break. 1 cell of 36 rows is 2.8% of the height — exactly the
 * lab's 3 steps x 8px of its 856px card. Same story across: 1 of 64 columns ≈ its 24px of 1520.
 */
const GAP = 1;
/** Corners within this summed RGBA distance of each other vote for the same ground colour. */
const CORNER_TOL = 24;

/**
 * Derives the reading-order delay map for one rendered card.
 *
 * Bands top to bottom, columns left to right inside a band, each part's cells stamped with its
 * normalized rank byte, and every remaining cell flooded from the nearest stamped one. `count: 0`
 * (all-zero delays) is the honest answer for a blank card — the caller shows no stagger at all.
 */
export function partsMapFrom(image: ImageDataLike, grid: PartsGrid = DEFAULT_PARTS_GRID): PartsMap {
  const gw = grid.w;
  const gh = grid.h;
  const delays = new Uint8Array(Math.max(0, gw * gh));
  const { data, width, height } = image;
  if (width <= 0 || height <= 0 || gw <= 0 || gh <= 0) return { delays, count: 0 };

  /* ── the ground: whatever colour the most corners agree on ─────────────────────────────────── */
  const cornerOffsets = [
    0,
    (width - 1) * 4,
    (height - 1) * width * 4,
    ((height - 1) * width + (width - 1)) * 4,
  ];
  let ground = cornerOffsets[0];
  let bestVotes = -1;
  for (const a of cornerOffsets) {
    let votes = 0;
    for (const b of cornerOffsets) {
      const dist =
        Math.abs(data[a] - data[b]) +
        Math.abs(data[a + 1] - data[b + 1]) +
        Math.abs(data[a + 2] - data[b + 2]) +
        Math.abs(data[a + 3] - data[b + 3]);
      if (dist <= CORNER_TOL) votes += 1;
    }
    // Ties fall to the earlier corner — top-left, which is where a card's margin actually is.
    if (votes > bestVotes) {
      bestVotes = votes;
      ground = a;
    }
  }
  const groundTransparent = data[ground + 3] <= ALPHA_MIN;
  const gr = data[ground];
  const gg = data[ground + 1];
  const gb = data[ground + 2];

  const isInk = (offset: number): boolean => {
    const a = data[offset + 3];
    if (a <= ALPHA_MIN) return false;
    if (groundTransparent) return true; // the lab's rule: on a transparent ground, presence is ink
    return (
      Math.abs(data[offset] - gr) + Math.abs(data[offset + 1] - gg) + Math.abs(data[offset + 2] - gb) >
      COLOR_MIN
    );
  };

  /* ── mark cells with content ───────────────────────────────────────────────────────────────── */
  // Sampling stride caps at ~8x8 probes per cell — element bounds, not letterforms, same altitude
  // as the lab's every-2nd-pixel walk of its 8px cells.
  const cells = new Uint8Array(gw * gh);
  for (let gy = 0; gy < gh; gy++) {
    const y0 = Math.floor((gy * height) / gh);
    const y1 = Math.max(y0 + 1, Math.floor(((gy + 1) * height) / gh));
    const sy = Math.max(1, Math.floor((y1 - y0) / 8));
    for (let gx = 0; gx < gw; gx++) {
      const x0 = Math.floor((gx * width) / gw);
      const x1 = Math.max(x0 + 1, Math.floor(((gx + 1) * width) / gw));
      const sx = Math.max(1, Math.floor((x1 - x0) / 8));
      let hit = 0;
      for (let y = y0; y < y1 && !hit; y += sy) {
        for (let x = x0; x < x1; x += sx) {
          if (isInk((y * width + x) * 4)) {
            hit = 1;
            break;
          }
        }
      }
      cells[gy * gw + gx] = hit;
    }
  }

  /* ── rows of ink → bands ───────────────────────────────────────────────────────────────────── */
  const rowHas = (gy: number): boolean => {
    for (let gx = 0; gx < gw; gx++) if (cells[gy * gw + gx]) return true;
    return false;
  };
  const bands: Array<[number, number]> = [];
  let bandStart = -1;
  let gapRun = 0;
  for (let gy = 0; gy < gh; gy++) {
    if (rowHas(gy)) {
      if (bandStart < 0) bandStart = gy;
      gapRun = 0;
    } else if (bandStart >= 0 && ++gapRun >= GAP) {
      bands.push([bandStart, gy - gapRun]);
      bandStart = -1;
    }
  }
  if (bandStart >= 0) bands.push([bandStart, gh - 1]);

  /* ── gaps inside a band → columns; the walk order IS the reading order ─────────────────────── */
  const parts: Array<{ x0: number; x1: number; y0: number; y1: number }> = [];
  for (const [top, bottom] of bands) {
    const colHas = (gx: number): boolean => {
      for (let gy = top; gy <= bottom; gy++) if (cells[gy * gw + gx]) return true;
      return false;
    };
    let colStart = -1;
    let run = 0;
    for (let gx = 0; gx < gw; gx++) {
      if (colHas(gx)) {
        if (colStart < 0) colStart = gx;
        run = 0;
      } else if (colStart >= 0 && ++run >= GAP) {
        parts.push({ x0: colStart, x1: gx - run, y0: top, y1: bottom });
        colStart = -1;
      }
    }
    if (colStart >= 0) parts.push({ x0: colStart, x1: gw - 1, y0: top, y1: bottom });
  }

  const count = parts.length;
  if (count === 0) return { delays, count: 0 };

  /* ── rank → byte, stamped over each part's box ─────────────────────────────────────────────── */
  // Later parts overwrite earlier where boxes overlap, exactly the lab's fillRect painting order.
  // Rank 0 gets byte 0 — the first element starts on the global clock, with no dead lead-in.
  const stamped = new Uint8Array(gw * gh);
  parts.forEach((part, rank) => {
    const value = Math.round((255 * rank) / count);
    for (let gy = part.y0; gy <= part.y1; gy++) {
      for (let gx = part.x0; gx <= part.x1; gx++) {
        const i = gy * gw + gx;
        delays[i] = value;
        stamped[i] = 1;
      }
    }
  });

  /* ── empty cells inherit the nearest ranked neighbour ──────────────────────────────────────── */
  // Multi-source BFS from every stamped cell at once: each blank cell takes the delay of whichever
  // part reaches it first, i.e. the nearest in grid distance. O(cells), one pass, no distance field.
  const queue = new Int32Array(gw * gh);
  let head = 0;
  let tail = 0;
  for (let i = 0; i < gw * gh; i++) if (stamped[i]) queue[tail++] = i;
  while (head < tail) {
    const i = queue[head++];
    const gx = i % gw;
    const value = delays[i];
    if (gx > 0 && !stamped[i - 1]) {
      stamped[i - 1] = 1;
      delays[i - 1] = value;
      queue[tail++] = i - 1;
    }
    if (gx < gw - 1 && !stamped[i + 1]) {
      stamped[i + 1] = 1;
      delays[i + 1] = value;
      queue[tail++] = i + 1;
    }
    if (i >= gw && !stamped[i - gw]) {
      stamped[i - gw] = 1;
      delays[i - gw] = value;
      queue[tail++] = i - gw;
    }
    if (i < gw * (gh - 1) && !stamped[i + gw]) {
      stamped[i + gw] = 1;
      delays[i + gw] = value;
      queue[tail++] = i + gw;
    }
  }

  return { delays, count };
}
